// Loom's answer for an open field — the ONE rule every surface that shows it
// reads (Focus Refine, the issue card and its ask pin, the /chat/:id inline
// card), plus the two small hooks that keep it closed and steady on screen.
//
// "Loom's answer, closed": wherever Loom has an answer for a field, no text box
// is open. The user sees Loom's text and two buttons — "Use this text", which is
// the user's answer in ONE click, and "Write my own", which is the only thing
// that opens a box. The click sends the exact bytes on screen on the lane a typed
// answer takes, so the gate grounds it exactly as it grounds typing: the echo is
// the user's turn, and nothing turns green without the user acting.

import { useCallback, useEffect, useRef, useState } from "react";

import { draftRowsOf } from "@loom/shared/readiness";

import { useRemountSafeState } from "../../state/remount-safe-state";
import { entrySeq } from "../timeline";

import {
  buildListAnswer,
  defaultWidgetForField,
  listHeadingForField,
} from "./answerMessage";

/** A row bullet, in every shape a value can come back wearing. The widget SENDS
 *  "- ", but a list field's value is rewritten by the model on a re-propose (the
 *  ready-card edit path) and comes back however that model writes a list. */
export const LIST_BULLET_RE = /^\s*(?:[-*•‣]|\d+[.)])\s+/u;

/** A line that titles the list rather than being an item of it: the widget's own
 *  self-label ("Acceptance criteria:") or a Markdown heading. Never a bullet row
 *  — a criterion may legitimately end in a colon. */
export const isListHeadingLine = (line: string): boolean =>
  !LIST_BULLET_RE.test(line) && (line.endsWith(":") || /^#{1,6}\s/u.test(line));

/** Split a list field's value back into its rows: drop the leading heading
 *  line(s), strip each row's bullet, trim, and drop empties. The inverse of the
 *  list widget's send serialization (`buildListAnswer`), and the ONE place that
 *  inverse lives — it re-seeds the list_builder, renders the ready card, and
 *  splits a settled red list into the rows Loom offers, so none of the three can
 *  disagree about what a row is.
 *
 *  Live, the ready card had its own inverse that only stripped a heading: after
 *  a field edit re-proposed the criteria in bulleted form, every row on the
 *  Accept screen read "- wer noch nie bestellt hat…", literal dash included. */
export const splitListRows = (value: string): string[] => {
  const lines = value.split("\n").map((l) => l.trim());
  let i = 0;
  while (i < lines.length && (lines[i] === "" || isListHeadingLine(lines[i]!))) i++;
  return lines
    .slice(i)
    .map((l) => l.replace(LIST_BULLET_RE, "").trim())
    .filter((l) => l !== "");
};

/** Is this field's value a serialized LIST (acceptance criteria / DoD)? Derived
 *  from the same widget table the answer control comes from, so a spec change
 *  moves both together. */
export const isListField = (field: string): boolean =>
  defaultWidgetForField(field).type === "list_builder";

/** Is this field answered by picking an option (priority, milestone)? */
export const isChoiceField = (field: string): boolean => {
  const type = defaultWidgetForField(field).type;
  return type === "single_select" || type === "dynamic_select";
};

/**
 * An answer that SETS a field's value: the three story clauses and the two
 * lists. A bare "yes" typed into one of these names nothing, so it is not sent
 * (or, over a value that already stands, it is a keep). Every other answer —
 * a scoping question, a concern, a note, the chat — keeps "ja" as a real answer.
 */
const VALUE_FIELDS: ReadonlySet<string> = new Set([
  "role",
  "means",
  "ends",
  "acceptance_criteria",
  "dod_criteria",
]);
export const isValueField = (field: string): boolean => VALUE_FIELDS.has(field);

/**
 * Loom's answer, in the shape the control shows it.
 *
 * `source: "draft"` — the typed `draft` rows on a `needs_input` slot or an ask.
 * `source: "unverified"` — the value on a red / unavailable verdict: the server
 * could not find it in the user's words, so it is Loom's own wording. Offered
 * only once SETTLED (see `loomAnswerOf`), under its own kicker.
 */
export type LoomAnswer =
  | {
      readonly kind: "text";
      readonly text: string;
      readonly source: "draft" | "unverified";
    }
  | {
      readonly kind: "list";
      readonly rows: ReadonlyArray<string>;
      readonly source: "draft" | "unverified";
    }
  | {
      readonly kind: "choice";
      readonly value: string;
      readonly source: "draft" | "unverified";
    };

export interface LoomAnswerInput {
  readonly verdict?: "green" | "red" | "needs_input" | "unavailable";
  readonly value?: string;
  readonly draft?: ReadonlyArray<string>;
  /** A concern is ABOUT the value as it stands: its only answer is its own
   *  one-row draft — Loom's suggested reply — and never the value itself. */
  readonly concern?: boolean;
  /** The user skipped this field. */
  readonly declined?: boolean;
}

/** Case- and whitespace-blind equality: "the same words". */
const sameWords = (a: string, b: string): boolean =>
  a.replace(/\s+/gu, " ").trim().toLowerCase() === b.replace(/\s+/gu, " ").trim().toLowerCase();

/**
 * Does Loom have an answer for this field, and which?
 *
 * The rules, in order:
 *   1. A declined field has none. A CONCERN has at most one: its own draft, if
 *      it is exactly one row and is not the value the concern questions — Loom's
 *      suggested REPLY (most often the rewording it is asking about), always
 *      shown as text, whatever the field. Never a red value, never the current
 *      value: #239 offered "so that yes" back as Loom's draft, and its one
 *      yes-shaped button wrote it to GitLab.
 *   2. The typed `draft` rows, counted with `draftRowsOf` — the SERVER's own
 *      counter, so a draft the server keeps is a draft this renders: a list
 *      needs one row or more, prose exactly one (a box cannot carry three
 *      alternatives — gluing them is a sentence nobody wrote), a choice its row.
 *   3. A red or unavailable value, but only when `settled` (the agent is idle
 *      and the card has already read the user's last answer): a list split with
 *      `splitListRows`, prose only if it is one line, a choice as is. Offered
 *      before it settled, a refusal the model is about to repair in its own turn
 *      would be put to the user as a question.
 *   4. Nothing else. A green value is the user's already, never Loom's.
 */
export const loomAnswerOf = (
  field: string,
  input: LoomAnswerInput,
  opts: { readonly settled: boolean },
): LoomAnswer | null => {
  if (input.declined === true) return null;
  const draft = (input.draft ?? []).filter((r) => r.trim() !== "");
  if (input.concern === true) {
    const rows = draftRowsOf(draft);
    if (rows.length !== 1) return null;
    const reply = rows[0]!;
    if (typeof input.value === "string" && sameWords(reply, input.value)) return null;
    return { kind: "text", text: reply, source: "draft" };
  }
  if (draft.length > 0) {
    const rows = draftRowsOf(draft);
    if (isListField(field)) {
      if (rows.length > 0) return { kind: "list", rows, source: "draft" };
    } else if (rows.length === 1) {
      return isChoiceField(field)
        ? { kind: "choice", value: rows[0]!, source: "draft" }
        : { kind: "text", text: rows[0]!, source: "draft" };
    }
  }
  if (
    opts.settled &&
    (input.verdict === "red" || input.verdict === "unavailable") &&
    typeof input.value === "string" &&
    input.value.trim() !== ""
  ) {
    const value = input.value.trim();
    if (isListField(field)) {
      const rows = splitListRows(value);
      return rows.length > 0 ? { kind: "list", rows, source: "unverified" } : null;
    }
    if (isChoiceField(field)) return { kind: "choice", value, source: "unverified" };
    return value.includes("\n") ? null : { kind: "text", text: value, source: "unverified" };
  }
  return null;
};

/** The bytes a click on Loom's answer sends — the same send-text a typed answer
 *  of that shape would carry, so the gate cannot tell the two apart. */
export const loomAnswerText = (field: string, answer: LoomAnswer): string =>
  answer.kind === "list"
    ? buildListAnswer(listHeadingForField(field), answer.rows)
    : answer.kind === "text"
      ? answer.text
      : answer.value;

/**
 * How long after a control mounts a POINTER click on it is ignored.
 *
 * The carry-over click: in Focus, "Use this text" advances to the next question,
 * whose own "Use this text" fades in under the cursor (460 ms). The second click
 * of a double-click would spend a draft the user never read. `detail > 1` catches
 * a real double-click (the OS counts clicks by time and place, not by DOM node);
 * this catches a fast second single click. Keyboard activation reports
 * `detail === 0` and is never a carry-over, so it is never delayed.
 */
export const ACCEPT_ARM_MS = 500;

/**
 * A guard for an accept / undo control: returns whether THIS activation may act.
 * Mount time is captured once per mount of the component that calls it.
 */
export function useArmedClick(): (e: { readonly detail: number }) => boolean {
  const mountedAt = useRef<number | null>(null);
  if (mountedAt.current === null) mountedAt.current = Date.now();
  return useCallback((e: { readonly detail: number }) => {
    if (e.detail > 1) return false;
    if (e.detail >= 1 && Date.now() - (mountedAt.current ?? 0) < ACCEPT_ARM_MS) {
      return false;
    }
    return true;
  }, []);
}

/**
 * Whether Loom's answer is shown CLOSED right now, and the two moves that change
 * that.
 *
 * `ownFor` records "the user chose to write their own" and is keyed to the offer
 * BYTES, remount-safe under `${draftKey}:ownFor`: a remount keeps the user's
 * choice, while a NEW draft is a new answer to read and closes the step again.
 *
 * It never closes a box the USER is in — one they clicked or typed into, or
 * tabbed to (`onBoxInteract`), while it still has the caret — and never one
 * holding text: an offer that ARRIVES while the box is open that way (a red
 * that just settled, a re-emit with a new draft) is latched open for its bytes,
 * and the widget shows it above the box as a reference instead.
 *
 * A caret the PAGE put there is not the user choosing anything. A step that
 * showed with no draft auto-focuses its empty box; when Loom's draft arrived a
 * moment later that focus alone latched the step open, so the user found
 * "Start from Loom's text" / "Back to Loom's draft" under a box they never
 * asked for (live, #246 and #260, 2026-09-25). Only "Write my own" opens it.
 * `closedOnCaret` says the step closed with the caret still in its box, so the
 * widget can hand focus to a control that does no harm instead of `body`.
 */
export function useClosedOffer({
  draftKey,
  offerKey,
  hasText,
}: {
  readonly draftKey: string | null;
  /** The offer's bytes (the exact send-text), or `null` for no offer. */
  readonly offerKey: string | null;
  /** The box (or the list) already holds the user's own text. */
  readonly hasText: boolean;
}): {
  readonly closed: boolean;
  readonly openOwn: () => void;
  readonly backToLoom: () => void;
  readonly onBoxFocus: () => void;
  readonly onBoxBlur: () => void;
  /** Pointer down, a key, or the key-up of the Tab that landed here: the user
   *  is in the box. */
  readonly onBoxInteract: () => void;
  /** The step closed while the (untouched) box had the caret. */
  readonly closedOnCaret: boolean;
} {
  const [ownFor, setOwnFor] = useRemountSafeState<string | null>(
    draftKey !== null ? `${draftKey}:ownFor` : null,
    null,
  );
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const engaged = focused && touched;
  const closed = offerKey !== null && !hasText && ownFor !== offerKey && !engaged;
  useEffect(() => {
    if (offerKey !== null && ownFor !== offerKey && (engaged || hasText)) {
      setOwnFor(offerKey);
    }
  }, [offerKey, ownFor, engaged, hasText, setOwnFor]);
  const openOwn = useCallback(() => setOwnFor(offerKey), [offerKey, setOwnFor]);
  const backToLoom = useCallback(() => {
    setOwnFor(null);
    setFocused(false);
    setTouched(false);
  }, [setOwnFor]);
  const onBoxFocus = useCallback(() => setFocused(true), []);
  const onBoxBlur = useCallback(() => {
    setFocused(false);
    setTouched(false);
  }, []);
  const onBoxInteract = useCallback(() => setTouched(true), []);
  // The box unmounts when the step closes, and a removed element's blur is not
  // reliably delivered — so a `focused` left over here IS "closed on the caret".
  const closedOnCaret = closed && focused;
  useEffect(() => {
    if (closedOnCaret) setFocused(false);
  }, [closedOnCaret]);
  return { closed, openOwn, backToLoom, onBoxFocus, onBoxBlur, onBoxInteract, closedOnCaret };
}

/** A user turn on the timeline, as far as the "has a card read it" rule needs. */
export interface UnreadUserTurn {
  readonly seq: number;
  /** The delivery id this client minted for it; absent on a turn it did not send. */
  readonly clientMsgId?: string;
}

/**
 * The user turns the card has NOT read: every `user_message` newer than the
 * card's grounding stamp (`groundedThroughSeq`), for this issue or bound to none
 * (`issueIid === null` reads every turn — a plain chat's answers go out
 * unbound). A card with no stamp (the degraded no-store path) is measured by its
 * own seq instead: a turn that landed after the card was emitted is one it
 * certainly did not read. No card, nothing to measure: every turn is unread.
 *
 * Read off the TIMELINE, not off what this mount remembers sending, because the
 * timeline is what a reload or a reconnect rebuilds: the echo is on the bus, so a
 * page that forgot it sent an answer still sees the answer. That is what keeps a
 * red from being offered as Loom's wording over the user's own words after a
 * reload — the card that proposed it predates them.
 */
export const unreadUserTurns = (
  timeline: ReadonlyArray<{
    readonly kind: string;
    readonly id: string;
    readonly issueIid?: number;
    readonly clientMsgId?: string;
  }>,
  card: { readonly id: string; readonly groundedThroughSeq?: number } | null | undefined,
  issueIid: number | null,
): ReadonlyArray<UnreadUserTurn> => {
  const cardStamp =
    card == null
      ? undefined
      : (card.groundedThroughSeq ??
        (Number.isFinite(entrySeq(card.id)) ? entrySeq(card.id) : undefined));
  const out: UnreadUserTurn[] = [];
  for (const e of timeline) {
    if (e.kind !== "user_message") continue;
    if (issueIid !== null && e.issueIid !== undefined && e.issueIid !== issueIid) continue;
    const seq = entrySeq(e.id);
    if (!Number.isFinite(seq)) continue;
    if (cardStamp !== undefined && seq <= cardStamp) continue;
    out.push({ seq, ...(e.clientMsgId !== undefined ? { clientMsgId: e.clientMsgId } : {}) });
  }
  return out;
};

/**
 * A red offer that stays up once it has settled.
 *
 * A red is offered only when settled, and the agent's NEXT turn makes the flow
 * busy again — without a latch, Loom's wording would vanish from under the user
 * the moment anything else started. Latched per (field, value): it goes when
 * that verdict or that value changes, or when the caller says the offer is
 * BLOCKED — a user turn that may have answered this field and that no card has
 * read yet. `settledNow` may only latch; `blocked` also releases.
 */
export function useRedOfferLatch(): (
  field: string,
  verdict: string | undefined,
  value: string | undefined,
  settledNow: boolean,
  blocked?: boolean,
) => boolean {
  const latch = useRef<Map<string, string>>(new Map());
  return useCallback(
    (field, verdict, value, settledNow, blocked = false) => {
      const red = verdict === "red" || verdict === "unavailable";
      if (!red || typeof value !== "string" || value.trim() === "") {
        latch.current.delete(field);
        return settledNow && !blocked;
      }
      if (blocked) {
        latch.current.delete(field);
        return false;
      }
      if (settledNow) {
        latch.current.set(field, value);
        return true;
      }
      return latch.current.get(field) === value;
    },
    [],
  );
}
